/**
 * Geração da Seção C - Cadastro da máquina
 * Consolida o conteúdo que estava na Seção F
 */

import { DadosApreciacao, Risco } from '../core/latex-types';
import { escapeLatex, escapeLatexComQuebras } from '../core/latex-utils';
import { gerarPlanoAcao } from '../tables/latex-tabelas';
import { gerarListaPartes } from '../generators/latex-lista-partes';
import { gerarListaVerificacaoAdministrativa } from './latex-lista-verificacao';

/**
 * Gera a subseção C.1 - Identificação da máquina
 */
function gerarSubsecaoC1IdentificacaoMaquina(dados: DadosApreciacao): string {
  if (!dados.maquina) {
    return '';
  }

  let latex = '';

  latex += `\\subsection*{C.1. Informações da máquina}
\\addcontentsline{toc}{subsection}{C.1. Informações da máquina}
\\label{sec:identificacao-maquina}

`;

  // Tabela de fotos — usar \vspace quando a imagem não está disponível
  const celulaPanoramica = dados.fotoPanoramicaUrl
    ? `\\parbox[c][6cm][b]{\\linewidth}{\\centering\\includegraphics[width=\\linewidth, height=6cm, keepaspectratio]{${dados.fotoPanoramicaUrl}}}`
    : `\\vspace{6cm}`;
  const celulaIdentificacao = dados.fotoIdentificacaoUrl
    ? `\\parbox[c][6cm][c]{\\linewidth}{\\centering\\includegraphics[width=0.9\\linewidth, height=6cm, keepaspectratio]{${dados.fotoIdentificacaoUrl}}}`
    : `\\vspace{6cm}`;

  latex += `\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}
\\renewcommand{\\tabularxcolumn}[1]{m{#1}}

\\begin{tabularx}{\\linewidth}{|>{\\centering\\arraybackslash}X|>{\\centering\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Foto panorâmica}} &
\\textcolor{white}{\\textbf{Foto de identificação}} \\\\
\\hline
${celulaPanoramica} & ${celulaIdentificacao} \\\\
\\hline
\\end{tabularx}
\\renewcommand{\\tabularxcolumn}[1]{p{#1}}
\\end{flushleft}

\\vspace{0.5cm}

`;

  // Construir linhas da tabela numerada - sempre incluir todos os itens
  const linhasTabela: Array<{ item: string; descricao: string }> = [];

  // C.1.1. TAG da máquina
  const tagMaquina = dados.identificacaoMaquina || dados.codigoInterno || dados.numeroDocumento || 'N/I';
  linhasTabela.push({ item: 'C.1.1. TAG da máquina', descricao: tagMaquina });

  // C.1.2. Localização
  // Priorizar o mesmo campo usado no formulário (maquina.local),
  // depois demais campos relacionados à localização
  const localizacao =
    dados.maquina.local ||
    dados.setorMaquina ||
    dados.maquina.area ||
    dados.maquina.site ||
    'N/I';
  linhasTabela.push({ item: 'C.1.2. Localização', descricao: localizacao });

  // C.1.3. Nome do fabricante
  const fabricante = dados.maquina.fabricante || 'N/I';
  linhasTabela.push({ item: 'C.1.3. Nome do fabricante', descricao: fabricante });

  // C.1.4. Modelo da máquina
  // Priorizar o modelo vindo da máquina, depois o tipo cadastrado no cabeçalho
  const modelo = dados.maquina.modelo || dados.tipoMaquina || 'N/I';
  linhasTabela.push({ item: 'C.1.4. Modelo da máquina', descricao: modelo });

  // C.1.5. Número de série
  const numeroSerie = dados.maquina?.serie || 'N/I';
  linhasTabela.push({ item: 'C.1.5. Número de série', descricao: numeroSerie });

  // C.1.6. Ano de fabricação
  const anoFabricacao = dados.maquina?.ano ? String(dados.maquina.ano) : 'N/I';
  linhasTabela.push({ item: 'C.1.6. Ano de fabricação', descricao: anoFabricacao });

  // C.1.7. Descrição da máquina
  // Usar apenas a descrição cadastrada na máquina para manter separação da apreciação
  const descricao = dados.maquina.descricao || 'N/I';
  linhasTabela.push({ item: 'C.1.7. Descrição da máquina', descricao: descricao });

  // C.1.8. Tensão de alimentação
  const tensaoAlimentacao = dados.tensaoAcionamento || 'N/I';
  linhasTabela.push({ item: 'C.1.8. Tensão de alimentação', descricao: tensaoAlimentacao });

  // C.1.9. Tensão de comando
  const tensaoComando = dados.tensaoComando || 'N/I';
  linhasTabela.push({ item: 'C.1.9. Tensão de comando', descricao: tensaoComando });

  // Tabela numerada
  latex += `\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}p{4.5cm}|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Item}} &
\\textcolor{white}{\\textbf{Descrição}} \\\\
\\hline
${linhasTabela.map(linha => `${escapeLatex(linha.item)} &
${escapeLatex(linha.descricao)} \\\\
\\hline`).join('\n')}
\\end{tabularx}
\\end{flushleft}

`;

  return latex;
}

/**
 * Gera a subseção C.2 - Responsável pela máquina
 */
function gerarSubsecaoC2ResponsavelMaquina(dados: DadosApreciacao): string {
  if (!dados.maquina) {
    return '';
  }

  // Formatar data da entrevista manualmente (evita problemas de timezone)
  const formatarDataPtBr = (valor: any): string | null => {
    if (!valor) return null;
    const str = String(valor).substring(0, 10);
    // Formato esperado: YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [ano, mes, dia] = str.split('-');
      return `${dia}/${mes}/${ano}`;
    }
    // Se não estiver no formato esperado, retornar string bruta
    return str;
  };

  // Usar primeiro a data da entrevista; se não houver, cair para data de emissão
  const dataEntrevistaFormatada =
    formatarDataPtBr(dados.dataEntrevista) ||
    formatarDataPtBr(dados.dataEmissao) ||
    'N/I';

  // Construir linhas da tabela numerada - sempre incluir todos os itens
  const linhasTabela: Array<{ item: string; descricao: string }> = [];

  // C.2.1. Nome
  const nome = dados.operadorEntrevistado || 'N/I';
  linhasTabela.push({ item: 'C.2.1. Nome', descricao: nome });

  // C.2.2. Função
  const funcao = dados.cargoOperador || 'N/I';
  linhasTabela.push({ item: 'C.2.2. Função', descricao: funcao });

  // C.2.3. Área
  const area = dados.maquina.area || 'N/I';
  linhasTabela.push({ item: 'C.2.3. Área', descricao: area });

  // C.2.4. Data da entrevista
  linhasTabela.push({ item: 'C.2.4. Data da entrevista', descricao: dataEntrevistaFormatada });

  // C.2.5. Empresa
  const empresa = dados.maquina.site || 'N/I';
  linhasTabela.push({ item: 'C.2.5. Empresa', descricao: empresa });

  let latex = `
\\subsection*{C.2. Responsável pela máquina}
\\addcontentsline{toc}{subsection}{C.2. Responsável pela máquina}
\\label{sec:responsavel-maquina}

`;

  latex += `\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}p{4.5cm}|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Item}} &
\\textcolor{white}{\\textbf{Descrição}} \\\\
\\hline
${linhasTabela.map(linha => `${escapeLatex(linha.item)} &
${escapeLatex(linha.descricao)} \\\\
\\hline`).join('\n')}
\\end{tabularx}
\\end{flushleft}

`;

  return latex;
}

/**
 * Gera a subseção C.3 - Limites da máquina
 */
function gerarSubsecaoC3LimitesMaquina(dados: DadosApreciacao): string {
  if (!dados.maquina) {
    return '';
  }

  // Construir linhas da tabela numerada - sempre incluir todos os itens
  const linhasTabela: Array<{ item: string; descricao: string; preservarQuebras?: boolean }> = [];

  // C.3.1. Limites de uso
  const limitesUso = dados.limitesUso || 'N/I';
  linhasTabela.push({ item: 'C.3.1. Limites de uso', descricao: limitesUso, preservarQuebras: true });

  // C.3.2. Limites de espaço
  const limitesEspaco = dados.limitesEspaco || 'N/I';
  linhasTabela.push({ item: 'C.3.2. Limites de espaço', descricao: limitesEspaco, preservarQuebras: true });

  // C.3.3. Limites de tempo
  const limitesTempo = dados.limitesAdicionais || 'N/I';
  linhasTabela.push({ item: 'C.3.3. Limites de tempo', descricao: limitesTempo, preservarQuebras: true });

  // C.3.4. Histórico de acidentes
  const historicoAcidentes = dados.historicoAcidentes || 'N/I';
  linhasTabela.push({ item: 'C.3.4. Histórico de acidentes', descricao: historicoAcidentes, preservarQuebras: true });

  let latex = `
\\subsection*{C.3. Limites da máquina}
\\addcontentsline{toc}{subsection}{C.3. Limites da máquina}
\\label{sec:limites-maquina}

`;

  latex += `\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash}p{4.5cm}|>{\\raggedright\\arraybackslash}X|}
\\hline
\\rowcolor{schmersalBlue}
\\textcolor{white}{\\textbf{Item}} &
\\textcolor{white}{\\textbf{Descrição}} \\\\
\\hline
${linhasTabela.map(linha => `${escapeLatex(linha.item)} &
${linha.preservarQuebras ? escapeLatexComQuebras(linha.descricao) : escapeLatex(linha.descricao)} \\\\
\\hline`).join('\n')}
\\end{tabularx}
\\end{flushleft}

\\vspace{0.3cm}

Nota: N/E - Não Evidenciado, N/I - Não Informado, N/A - Não Aplicável

`;

  return latex;
}

/**
 * Gera a subseção C.4 - Lista de verificação administrativa da máquina
 */
function gerarSubsecaoC4ListaVerificacaoAdministrativa(dados: DadosApreciacao): string {
  return gerarListaVerificacaoAdministrativa(dados);
}

/**
 * Gera a subseção C.5 - Plano de ação para mitigação
 */
function gerarSubsecaoC5PlanoAcaoMitigacao(riscos: Risco[]): string {
  const planoAcao = gerarPlanoAcao(riscos);
  // Remover o título "PLANO DE AÇÃO PARA Apreciações de riscos" e o \newpage inicial (se existir)
  const planoAcaoSemTitulo = planoAcao
    .replace(/\\newpage\s*/, '')
    .replace(/.*PLANO DE AÇÃO PARA Apreciações de riscos.*\\end\{center\}.*/s, '')
    .trim();

  return `
\\subsection*{C.5. Plano de ação para mitigação}
\\addcontentsline{toc}{subsection}{C.5. Plano de ação para mitigação}

${planoAcaoSemTitulo}
`;
}

/**
 * Gera a subseção C.6 - Lista de partes
 */
function gerarSubsecaoC6ListaPartes(riscos: Risco[], dados: DadosApreciacao): string {
  if (!riscos || riscos.length === 0) {
    return '';
  }
  const referencia = dados.numeroEmitente || dados.ativo?.refSismanaNovo || dados.ativo?.refSismanaAntigo || '';
  const listaPartes = gerarListaPartes(riscos, referencia);
  // Remover apenas o \newpage inicial, mantendo a tabela azul
  let listaPartesAjustada = listaPartes.replace(/\\newpage\s*/, '');

  // Adicionar título da subseção antes do label
  listaPartesAjustada = listaPartesAjustada.replace(
    /\\label\{sec:checklist\}/,
    '\\subsection*{C.6. Sumário de partes}\n\\addcontentsline{toc}{subsection}{C.6. Sumário de partes}\n\\label{sec:checklist}'
  );

  return listaPartesAjustada;
}

/**
 * Gera a tabela de riscos adicionais da NR-12 (item 12.10.1) usada na C.7,
 * relacionando cada alínea ao documento de referência e ao responsável.
 */
function gerarTabelaRiscosAdicionais(): string {
  return `
\\begin{table}[H]
\\centering
\\begin{flushleft}
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}

\\begin{tabularx}{\\linewidth}{|>{\\raggedright\\arraybackslash\\hsize=1.45\\hsize}X|>{\\raggedright\\arraybackslash\\hsize=0.85\\hsize}X|>{\\centering\\arraybackslash\\hsize=0.7\\hsize}X|}
\\hline
\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{Risco Adicional (NR-12, 12.10.1)}} &
\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{Documento de Referência}} &
\\cellcolor{schmersalBlue}\\textcolor{white}{\\textbf{Responsável}} \\\\ \\hline
a) Substâncias perigosas (químicos/biológicos) & LTCAT / PCMSO / PGR & SESMT \\\\ \\hline
b) Radiações ionizantes e não ionizantes & LTCAT / PGR & SESMT \\\\ \\hline
c) Vibrações & LTCAT / PGR & SESMT \\\\ \\hline
d) Ruído & LTCAT / PGR / PCA & SESMT \\\\ \\hline
e) Calor & LTCAT / PGR & SESMT \\\\ \\hline
f) Combustíveis, inflamáveis e explosivos & PGR / LTCAT & SESMT \\\\ \\hline
g) Superfícies aquecidas & PGR / Procedimento Operacional & SESMT \\\\ \\hline
\\end{tabularx}
\\end{flushleft}
\\end{table}

`;
}

/**
 * Gera a subseção C.7 - Delimitações do relatório
 */
function gerarSubsecaoC7DelimitacoesRelatorio(): string {
  return `
\\newpage

\\subsection*{C.7. Delimitações do relatório}
\\addcontentsline{toc}{subsection}{C.7. Delimitações do relatório}

As funcionalidades descritas da máquina ou equipamento nesta documentação são baseadas nos dados fornecidos pelo fabricante ou pelo proprietário da máquina e as informações coletadas no local durante a apreciação de riscos que aconteceu na data informada nos dados na seção C. As ilustrações representadas e fotos da máquina referem-se às condições registradas no momento da inspeção.
\\\\\\\\
\\par
A SCHMERSAL salienta que a lista de partes ou atividades deste documento de apreciação de riscos destina-se a auxiliar as pessoas responsáveis e/ou seus representantes no cumprimento dos requisitos legais da NR 12.
\\\\\\\\
\\par
Para máquina nova ou que será adequada a partir da recomendação deste documento, a SCHMERSAL não assegura ou confirma que a adequação será executada conforme solicitado.
\\\\\\\\
\\par
Outros perigos citados na ABNT NBR ISO 12100 não estão sendo avaliados pois necessitam de uma avaliação específica. Por exemplo, atividades como: o transporte aéreo de cargas, os ambientes de espaço confinado, a análise de uso de EPIs, o laudo de ancoragem, a ergonomia, os vasos de pressão, a infraestrutura civil, a classificação da área (IP e EX), perigos associados ao ambiente e a estabilidade da máquina. Estes itens são apontados no Checklist Técnico e Administrativo.
\\par\\bigskip
\\noindent Importante salientar que a Apreciação de Riscos apresentada contempla os perigos identificados na máquina por meio de exame visual conforme a ABNT NBR ISO 12100, abrangendo, quando aplicáveis, os perigos mecânicos, elétricos, térmicos e demais perigos pertinentes às partes e atividades avaliadas, em atendimento aos requisitos da NR-12. Os perigos que demandam avaliação específica encontram-se relacionados nas delimitações do relatório (Seção C.7).
\\par\\medskip
\\noindent Os riscos físicos, químicos e biológicos associados a esta máquina foram levantados, caracterizados e quantificados no LTCAT (Laudo Técnico das Condições Ambientais de Trabalho) e encontram-se devidamente gerenciados no PGR (Programa de Gerenciamento de Riscos) da Segurança e Saúde do Trabalho da ArcelorMittal Tubarão, em conformidade com o item 1.5.5.1 da NR-01 e a Portaria SEPRT nº 6.730/2020, documento este que integra a presente Apreciação de Riscos como referência normativa obrigatória.
\\par\\medskip
\\noindent Analogamente, os riscos ergonômicos associados à operação desta máquina foram identificados e avaliados na Análise Ergonômica do Trabalho (AET), elaborada nos termos da NR-17 e suas atualizações, sendo igualmente gerenciados no PGR da unidade, assegurando a abordagem integrada e multidisciplinar dos fatores de risco ocupacionais.
\\par\\medskip
\\noindent Este documento abrange a vida útil da máquina avaliada, a partir da data de fabricação, conforme padrão do Instituto Brasileiro de Avaliações e Perícias de Engenharia de São Paulo (IBAPE/SP), caso não exista esta informação descrita pelo fabricante.
\\par\\bigskip
\\noindent\\textbf{Riscos Adicionais (NR-12, Item 12.10)}
\\par\\medskip
\\noindent De igual modo, os riscos adicionais previstos no item 12.10 e subitem 12.10.1, alíneas "a" a "h" da NR-12 não integram o escopo desta apreciação de riscos, por demandarem avaliações específicas, conforme a seguinte sistemática de gestão documental:

${gerarTabelaRiscosAdicionais()}

\\noindent\\textbf{Integração com o Sistema de Gestão de SST}
\\par\\medskip
\\noindent Os riscos físicos, químicos e biológicos associados a esta máquina --- incluindo aqueles listados no item 12.10 da NR-12 --- foram levantados, caracterizados e quantificados no LTCAT (Laudo Técnico das Condições Ambientais de Trabalho) e encontram-se devidamente gerenciados no PGR (Programa de Gerenciamento de Riscos) da Segurança e Saúde do Trabalho da ArcelorMittal Tubarão, em conformidade com o item 1.5.5.1 da NR-01 e a Portaria SEPRT nº 6.730/2020.
\\par\\medskip
\\noindent O risco ergonômico associado à operação desta máquina foi identificado e avaliado na Análise Ergonômica do Trabalho (AET), elaborada nos termos da NR-17 e suas atualizações, sendo igualmente gerenciado no PGR da unidade.
\\par\\medskip
\\noindent Tais avaliações específicas e respectivos documentos complementares LTCAT, PGR, AET, PCMSO e PCA não fazem parte do escopo do presente relatório, constituindo documentos autônomos e independentes, elaborados e mantidos pelo Serviço Especializado em Engenharia de Segurança e em Medicina do Trabalho (SESMT) da ArcelorMittal Tubarão, permanecendo à disposição dos órgãos fiscalizadores e auditorias para consulta e verificação.

\\bigskip
\\noindent A SCHMERSAL não aceita responsabilidade pela forma que este documento de Apreciação de Riscos possa ser utilizado ou por qualquer conteúdo alterado após entrega na data descrita. Todo esforço é feito para assegurar que as informações contidas neste documento sejam exatas e atualizadas utilizando diretrizes, normas técnicas e regulamentações relevantes para essa(s) máquina(s). A responsabilidade da Schmersal se limita às informações e termos descritos neste documento.

`;
}

/**
 * Gera a Seção C completa - Cadastro da máquina
 */
export function gerarSecaoCCadastroMaquina(dados: DadosApreciacao, riscos: Risco[]): string {
  if (!dados.maquina) {
    return '';
  }



  let latex = `
\\newpage

\\noindent\\section*{Seção C - Cadastro da máquina}
\\addcontentsline{toc}{section}{C. Cadastro da máquina}
\\label{sec:cadastro-maquina}


\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{8pt}


`;

  // Adicionar subseções
  latex += gerarSubsecaoC1IdentificacaoMaquina(dados);
  latex += gerarSubsecaoC2ResponsavelMaquina(dados);
  latex += gerarSubsecaoC3LimitesMaquina(dados);
  latex += gerarSubsecaoC4ListaVerificacaoAdministrativa(dados);
  latex += gerarSubsecaoC5PlanoAcaoMitigacao(riscos || []);
  latex += gerarSubsecaoC6ListaPartes(riscos, dados);
  latex += gerarSubsecaoC7DelimitacoesRelatorio();

  return latex;
}
